import React, { useEffect, useState, useMemo, useContext } from "react"
import { SessionContext } from "context/sessionContext"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Col,
  Container,
  Row,
  Table,
  Button,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { classApi } from "apis/ClassListApi"
import { sessionApi } from "apis/SessionApi"
import { studentRemarksApi } from "apis/StudentRemarksApi"

const ITEMS_PER_PAGE = 10

const StudentRemark = () => {
  document.title = "Student Remark"
  const [selectedClass, setSelectedClass] = useState(null)
  const [reportData, setReportData] = useState([])
  const [hasGenerated, setHasGenerated] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)
  const [appliedSearch, setAppliedSearch] = useState("")

  const { Session } = useContext(SessionContext)
  const activeSessionId = Session || sessionStorage.getItem("SessionId")

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // Class list
  const [classList, setClassList] = useState([])
  const [classListError, setClassListError] = useState("")

  // Reset student states and selection when session changes
  useEffect(() => {
    setReportData([])
    setHasGenerated(false)
    setSearchQuery("")
    setAppliedSearch("")
    setCurrentPage(1)
    setSelectedClass(null)
  }, [activeSessionId])

  // ── Fetch class list ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await classApi.getAllClass()
        if (res?.data?.section_list && Array.isArray(res.data.section_list)) {
          const unique = res.data.section_list
            .map(cl => ({
              value: cl.UID,
              label: `${cl.st_class.toUpperCase()} / ${cl.st_sec.toUpperCase()}`,
              st_class: cl.st_class,
              st_sec: cl.st_sec,
            }))
            .filter(
              (item, index, arr) =>
                index === arr.findIndex(d => d.label === item.label)
            )
          setClassList(unique)
        } else {
          setClassListError("Class list could not be loaded.")
        }
      } catch (err) {
        console.error("Failed to fetch class list:", err.message)
        setClassListError(
          "Failed to load classes. Please refresh and try again."
        )
      }
    }
    fetchClasses()
  }, [])

  const selectStyles = {
    menu: provided => ({ ...provided, backgroundColor: "#fff", zIndex: 1000 }),
    menuList: provided => ({
      ...provided,
      backgroundColor: "#fff",
      maxHeight: "200px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
      color: "#000",
      cursor: "pointer",
    }),
  }

  // ── Fetch remarks ────────────────────────────────────────────────────────
  const fetchRemarks = async (page = 1, search = "") => {
    if (!activeSessionId || !selectedClass) return
    setIsLoading(true)
    try {
      const payload = {
        session_id: activeSessionId ? parseInt(activeSessionId, 10) : null,
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        page,
        ...(search.trim() ? { search: search.trim() } : {}),
      }
      const res = await studentRemarksApi.getAllRemarks(payload)
      if (res?.data?.status === 200 && Array.isArray(res.data.students)) {
        setReportData(res.data.students)
        setTotalPages(res.data.total_pages ?? 1)
        setTotalCount(res.data.total_count ?? res.data.students.length)
        setCurrentPage(res.data.page_number ?? page)
        setHasGenerated(true)
      } else {
        Swal.fire("Error", res?.msg || "Failed to fetch remarks.", "error")
      }
    } catch (err) {
      console.error("Failed to fetch remarks:", err.message)
      Swal.fire("Error", "Failed to fetch remarks. Please try again.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleGenerateReport = async () => {
    if (!selectedClass) {
      Swal.fire("Validation", "Please select a Class / Section.", "warning")
      return
    }
    setSearchQuery("")
    setIsGenerating(true)
    await fetchRemarks(1, "")
    setIsGenerating(false)
  }

  const handleReset = () => {
    setSelectedClass(null)
    setReportData([])
    setHasGenerated(false)
    setSearchQuery("")
    setCurrentPage(1)
    setTotalPages(1)
    setTotalCount(0)
    setSearchQuery("")
    setAppliedSearch("")
  }

  useEffect(() => {
    if (!hasGenerated) return
    if (searchQuery === appliedSearch) return

    const delayDebounceFn = setTimeout(() => {
      setAppliedSearch(searchQuery)
      fetchRemarks(1, searchQuery)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery, hasGenerated, appliedSearch])

  const handleSearch = () => {
    if (!hasGenerated) return

    setAppliedSearch(searchQuery)
    fetchRemarks(1, searchQuery)
  }
  const handleSearchKeyDown = e => {
    if (e.key === "Enter") handleSearch()
  }

  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    fetchRemarks(page, searchQuery)
  }

  // ── Derived data ─────────────────────────────────────────────────────────
  const filteredReportData = useMemo(() => {
    const q = appliedSearch.trim().toLowerCase()
    if (!q) return reportData
    return reportData.filter(
      student =>
        student.st_code?.toLowerCase().includes(q) ||
        student.st_name?.toLowerCase().includes(q)
    )
  }, [reportData, appliedSearch])

  const filterSummary = {
    class: selectedClass?.label || "—",
  }

  // ── Export Report ────────────────────────────────────────────────────────
  const handleExport = async (format) => {
    if (!activeSessionId || !selectedClass) {
      Swal.fire("Validation", "Please select a Class / Section.", "warning")
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        session_id: activeSessionId ? parseInt(activeSessionId, 10) : null,
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        export_format: format,
        ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
      }

      const res = await studentRemarksApi.exportRemarksReport(payload)
      
      // If the response is JSON (usually an error message), don't download it
      if (res.data && res.data.type === "application/json") {
        const text = res.data.text ? await res.data.text() : await new Promise(r => {
          const reader = new FileReader()
          reader.onload = () => r(reader.result)
          reader.readAsText(res.data)
        })
        try {
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
        } catch (e) {
          Swal.fire("Error", "Failed to export report.", "error")
        }
        return
      }

      const fileType = format === "excel" 
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
        : "application/pdf"
      const fileExtension = format === "excel" ? "xlsx" : "pdf"
      
      const blob = new Blob([res.data], { type: fileType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `Student_Annual_Remarks_Report.${fileExtension}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Export failed:", err)
      if (err.response?.data && err.response.data instanceof Blob && err.response.data.type === "application/json") {
        try {
          const text = err.response.data.text ? await err.response.data.text() : await new Promise(r => {
            const reader = new FileReader()
            reader.onload = () => r(reader.result)
            reader.readAsText(err.response.data)
          })
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
          return
        } catch (e) {
          // fallback
        }
      }
      Swal.fire("Error", "Failed to export report.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleExportExcel = () => handleExport("excel")
  const handleExportPDF = () => handleExport("pdf")

  // ── Table body ───────────────────────────────────────────────────────────
  const renderTableBody = () => {
    if (!selectedClass) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-info-circle"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            To view annual remarks, please select a{" "}
            <strong>Class / Section</strong>.
          </td>
        </tr>
      )
    }
    if (!hasGenerated && !isLoading) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-bar-chart-alt-2"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Click <strong>Generate Report</strong> to load the remarks.
          </td>
        </tr>
      )
    }
    if (isLoading) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-loader-alt bx-spin"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Loading remarks...
          </td>
        </tr>
      )
    }
    if (filteredReportData.length === 0) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-search-alt"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            {searchQuery
              ? "No students match your search."
              : "No annual remarks found for the selected criteria."}
          </td>
        </tr>
      )
    }
    return filteredReportData.map((student, index) => (
      <tr key={student.st_code} className="text-center">
        <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
        <td>{student.st_code}</td>
        <td className="text-start">{student.st_name}</td>
        <td className="text-start">
          {student.remark || (
            <span className="text-muted fst-italic">No remark</span>
          )}
        </td>
      </tr>
    ))
  }

  // ── Pagination ───────────────────────────────────────────────────────────
  const renderPagination = () => {
    if (!hasGenerated || totalPages <= 1) return null
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing page <strong>{currentPage}</strong> of{" "}
          <strong>{totalPages}</strong>
          &nbsp;|&nbsp; Total: <strong>{totalCount}</strong> students
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>
              «
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
          </li>
          {pages.map(p => (
            <li
              key={p}
              className={`page-item ${p === currentPage ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => handlePageChange(p)}>
                {p}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </li>
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </button>
          </li>
        </ul>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid={true}>
            <Breadcrumb
              title="Examination"
              breadcrumbItem="Annual Remarks Report"
            />

            {/* ── Filter Section ── */}
            <div className="p-3 mb-4 rounded bg-white">
              <Row>
                <Col lg={4} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Class / Section <span className="text-danger">*</span>
                  </label>
                  {classListError ? (
                    <div
                      className="text-danger"
                      style={{ fontSize: "13px", marginTop: "8px" }}
                    >
                      <i className="bx bx-error-circle me-1" />
                      {classListError}
                    </div>
                  ) : (
                    <Select
                      name="st_class"
                      classNamePrefix="select2-selection"
                      placeholder="Select Class / Section"
                      options={classList}
                      value={selectedClass}
                      onChange={val => {
                        setSelectedClass(val)
                        setHasGenerated(false)
                        setReportData([])
                      }}
                      styles={selectStyles}
                    />
                  )}
                </Col>
              </Row>

              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleGenerateReport}
                    className="me-2"
                    disabled={isLoading || isGenerating}
                  >
                    <i className="bx bx-bar-chart-alt-2 me-1" />
                    {isGenerating ? "Loading..." : "Generate Report"}
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    <i className="bx bx-reset me-1" />
                    Reset
                  </Button>
                </Col>
              </Row>
            </div>

            {/* ── Actions & Search Row ── */}
            <Row className="mb-3 align-items-center">
              {/* Left: Search */}
              <Col lg={5} md={12} className="mb-2 mb-lg-0">
                <div className="d-flex gap-2">
                  <div style={{ position: "relative", width: "100%" }}>
                    <Input
                      type="text"
                      placeholder="Search by Student Name or Code"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      disabled={!hasGenerated}
                      style={{ fontSize: "13px", paddingRight: "30px" }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery("")
                          setAppliedSearch("")
                          fetchRemarks(1, "")
                        }}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          fontSize: "12px",
                          padding: 0,
                          color: "#6c757d",
                          zIndex: 5,
                        }}
                      >
                        ❌
                      </button>
                    )}
                  </div>
                  <Button
                    color="primary"
                    onClick={handleSearch}
                    disabled={!hasGenerated || isLoading}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <i className="bx bx-search me-1" />
                    Search
                  </Button>
                </div>
              </Col>

              {/* Right: Export */}
              <Col lg={7} md={12}>
                <div className="d-flex justify-content-lg-end">
                  <Dropdown
                    isOpen={exportDropdownOpen}
                    toggle={() => setExportDropdownOpen(prev => !prev)}
                    disabled={!hasGenerated}
                  >
                    <DropdownToggle
                      color="dark"
                      size="md"
                      caret
                      disabled={!hasGenerated}
                    >
                      <i className="bx bx-export me-1" />
                      Export
                    </DropdownToggle>
                    <DropdownMenu end>
                      <DropdownItem onClick={handleExportExcel}>
                        <i className="bx bx-file me-2 text-success" />
                        Export Excel
                      </DropdownItem>
                      <DropdownItem onClick={handleExportPDF}>
                        <i className="bx bxs-file-pdf me-2 text-danger" />
                        Export PDF
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </Col>
            </Row>

            {/* ── Table ── */}
            <div className="table-responsive">
              <Table className="table table-striped table-bordered">
                <thead>
                  <tr className="text-center table-secondary">
                    <th style={{ width: "60px" }}>#</th>
                    <th style={{ width: "120px" }}>Student Code</th>
                    <th className="text-start" style={{ width: "220px" }}>
                      Student Name
                    </th>
                    <th className="text-start">Annual Remark</th>
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </Table>
            </div>
            {renderPagination()}
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default StudentRemark
